import { useState, useRef, useEffect } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Plus, Trash2, Save, Play, GripVertical, ChevronRight,
  Edit2, Copy, Zap, ArrowRight, Layers, X, Info,
} from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PromptNode {
  id: string;
  name: string;
  model: string;
  maxTokens: number;
  prompt: string;
  outputKey: string;
}

export interface PipelineStage {
  id: string;
  label: string;
  nodes: PromptNode[];
}

export interface CustomPipeline {
  id: string;
  name: string;
  stages: PipelineStage[];
}

// ─── Constants ────────────────────────────────────────────────────────────────

const MODELS = [
  { value: "claude-haiku-4-5", label: "Haiku 4.5 — Fast & cheap" },
  { value: "claude-sonnet-4-6", label: "Sonnet 4.6 — Balanced" },
  { value: "claude-sonnet-5",   label: "Sonnet 5 — Strongest" },
];

const SYSTEM_VARS = ["RESUME", "JOB_DESCRIPTION", "SUPPORTING_DOCS", "SPECIAL_INSTRUCTIONS"];

const EMPTY_NODE = (): PromptNode => ({
  id: crypto.randomUUID(),
  name: "New Prompt",
  model: "claude-haiku-4-5",
  maxTokens: 4000,
  prompt: "",
  outputKey: "output_" + Date.now(),
});

const EMPTY_STAGE = (): PipelineStage => ({
  id: crypto.randomUUID(),
  label: "New Stage",
  nodes: [],
});

const DEFAULT_PIPELINE: CustomPipeline = {
  id: crypto.randomUUID(),
  name: "My Pipeline",
  stages: [
    {
      id: crypto.randomUUID(),
      label: "Stage 1 — Prep",
      nodes: [
        {
          id: crypto.randomUUID(),
          name: "JD Organizer",
          model: "claude-haiku-4-5",
          maxTokens: 4000,
          prompt: "Analyze this job description and extract the key requirements, skills, and responsibilities.\n\nJOB DESCRIPTION:\n{{JOB_DESCRIPTION}}",
          outputKey: "organized_jd",
        },
      ],
    },
    {
      id: crypto.randomUUID(),
      label: "Stage 2 — Sections",
      nodes: [
        {
          id: crypto.randomUUID(),
          name: "Career Summary",
          model: "claude-sonnet-4-6",
          maxTokens: 6000,
          prompt: "Write a 4-sentence career summary tailored to this role.\n\nRESUME:\n{{RESUME}}\n\nJD ANALYSIS:\n{{organized_jd}}",
          outputKey: "career_summary",
        },
        {
          id: crypto.randomUUID(),
          name: "Skills Section",
          model: "claude-haiku-4-5",
          maxTokens: 2000,
          prompt: "Rewrite the skills section to match this job.\n\nRESUME:\n{{RESUME}}\n\nJD ANALYSIS:\n{{organized_jd}}",
          outputKey: "skills_section",
        },
      ],
    },
    {
      id: crypto.randomUUID(),
      label: "Stage 3 — Assembly",
      nodes: [
        {
          id: crypto.randomUUID(),
          name: "Final Assembly",
          model: "claude-sonnet-4-6",
          maxTokens: 8000,
          prompt: "Assemble the final tailored resume combining all sections.\n\nORIGINAL RESUME:\n{{RESUME}}\n\nCAREER SUMMARY:\n{{career_summary}}\n\nSKILLS:\n{{skills_section}}",
          outputKey: "final_resume",
        },
      ],
    },
  ],
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function uid() { return crypto.randomUUID(); }

// ─── Node Card ────────────────────────────────────────────────────────────────

function NodeCard({
  node, stageId, pipeline,
  onEdit, onDelete, onDragStart, onDragOver, onDrop,
}: {
  node: PromptNode; stageId: string; pipeline: CustomPipeline;
  onEdit: (stageId: string, node: PromptNode) => void;
  onDelete: (stageId: string, nodeId: string) => void;
  onDragStart: (e: React.DragEvent, stageId: string, nodeId: string) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent, targetStageId: string, targetNodeId?: string) => void;
}) {
  const modelLabel = MODELS.find(m => m.value === node.model)?.label.split("—")[0].trim() ?? node.model;

  return (
    <div
      draggable
      onDragStart={e => onDragStart(e, stageId, node.id)}
      onDragOver={onDragOver}
      onDrop={e => onDrop(e, stageId, node.id)}
      className="group bg-white border border-gray-200 rounded-lg p-3 cursor-grab active:cursor-grabbing hover:border-primary/40 hover:shadow-md transition-all"
    >
      <div className="flex items-start gap-2">
        <GripVertical className="w-4 h-4 text-gray-300 mt-0.5 flex-shrink-0 group-hover:text-gray-400" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-1">
            <span className="font-semibold text-sm text-gray-800 truncate">{node.name}</span>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button onClick={() => onEdit(stageId, node)} className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600">
                <Edit2 className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => onDelete(stageId, node.id)} className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            <Badge variant="outline" className="text-xs px-1.5 py-0 h-5 font-normal text-blue-600 border-blue-200 bg-blue-50">
              {modelLabel}
            </Badge>
            <Badge variant="outline" className="text-xs px-1.5 py-0 h-5 font-normal text-gray-500">
              {node.maxTokens.toLocaleString()} tok
            </Badge>
          </div>
          <div className="mt-1.5 text-xs text-gray-400 font-mono truncate">
            → <span className="text-green-600">{`{{${node.outputKey}}}`}</span>
          </div>
          {node.prompt && (
            <p className="mt-1.5 text-xs text-gray-400 line-clamp-2 leading-relaxed">
              {node.prompt.slice(0, 120)}{node.prompt.length > 120 ? "…" : ""}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Stage Column ─────────────────────────────────────────────────────────────

function StageColumn({
  stage, index, total, pipeline,
  onAddNode, onDeleteStage, onRenameStage,
  onEditNode, onDeleteNode,
  onDragStart, onDragOver, onDrop, onDropOnStage,
}: {
  stage: PipelineStage; index: number; total: number; pipeline: CustomPipeline;
  onAddNode: (stageId: string) => void;
  onDeleteStage: (stageId: string) => void;
  onRenameStage: (stageId: string, label: string) => void;
  onEditNode: (stageId: string, node: PromptNode) => void;
  onDeleteNode: (stageId: string, nodeId: string) => void;
  onDragStart: (e: React.DragEvent, stageId: string, nodeId: string) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent, targetStageId: string, targetNodeId?: string) => void;
  onDropOnStage: (e: React.DragEvent, targetStageId: string) => void;
}) {
  const [editingLabel, setEditingLabel] = useState(false);
  const [labelVal, setLabelVal] = useState(stage.label);

  return (
    <div className="flex items-start gap-0">
      {/* Stage column */}
      <div
        className="w-64 flex-shrink-0"
        onDragOver={e => e.preventDefault()}
        onDrop={e => onDropOnStage(e, stage.id)}
      >
        {/* Stage header */}
        <div className="flex items-center justify-between mb-2 px-1">
          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            <Layers className="w-4 h-4 text-primary flex-shrink-0" />
            {editingLabel ? (
              <input
                autoFocus
                className="text-sm font-semibold flex-1 border-b border-primary outline-none bg-transparent"
                value={labelVal}
                onChange={e => setLabelVal(e.target.value)}
                onBlur={() => { setEditingLabel(false); onRenameStage(stage.id, labelVal); }}
                onKeyDown={e => { if (e.key === "Enter") { setEditingLabel(false); onRenameStage(stage.id, labelVal); } }}
              />
            ) : (
              <span
                className="text-sm font-semibold text-gray-700 cursor-pointer hover:text-primary truncate"
                onClick={() => setEditingLabel(true)}
              >
                {stage.label}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <span className="text-xs text-gray-400 bg-gray-100 rounded px-1.5 py-0.5">
              {stage.nodes.length} {stage.nodes.length === 1 ? "node" : "nodes"}
            </span>
            {total > 1 && (
              <button
                onClick={() => onDeleteStage(stage.id)}
                className="p-1 rounded hover:bg-red-50 text-gray-300 hover:text-red-500"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Parallel label */}
        {stage.nodes.length > 1 && (
          <div className="text-xs text-center text-purple-500 font-medium mb-2 flex items-center justify-center gap-1">
            <Zap className="w-3 h-3" />
            Runs simultaneously
          </div>
        )}

        {/* Nodes */}
        <div className="space-y-2 min-h-[80px] rounded-lg border-2 border-dashed border-gray-200 p-2">
          {stage.nodes.map(node => (
            <NodeCard
              key={node.id}
              node={node}
              stageId={stage.id}
              pipeline={pipeline}
              onEdit={onEditNode}
              onDelete={onDeleteNode}
              onDragStart={onDragStart}
              onDragOver={onDragOver}
              onDrop={onDrop}
            />
          ))}
          {stage.nodes.length === 0 && (
            <div className="text-xs text-gray-400 text-center py-4">
              Drop a node here
            </div>
          )}
        </div>

        <button
          onClick={() => onAddNode(stage.id)}
          className="mt-2 w-full text-xs text-primary border border-dashed border-primary/40 rounded-lg py-2 hover:bg-primary/5 hover:border-primary transition-colors flex items-center justify-center gap-1"
        >
          <Plus className="w-3 h-3" /> Add Prompt
        </button>
      </div>

      {/* Arrow connector */}
      {index < total - 1 && (
        <div className="flex items-center self-center px-2 mt-4">
          <ArrowRight className="w-5 h-5 text-gray-300" />
        </div>
      )}
    </div>
  );
}

// ─── Node Editor Dialog ───────────────────────────────────────────────────────

function NodeEditorDialog({
  node, pipeline, open, onClose, onSave,
}: {
  node: PromptNode | null; pipeline: CustomPipeline; open: boolean;
  onClose: () => void; onSave: (updated: PromptNode) => void;
}) {
  const [form, setForm] = useState<PromptNode | null>(null);

  useEffect(() => {
    if (node) setForm({ ...node });
  }, [node]);

  if (!form) return null;

  // Collect all output keys from all stages (for variable reference hints)
  const allOutputKeys = pipeline.stages.flatMap(s => s.nodes.map(n => n.outputKey));

  const insertVar = (v: string) => {
    setForm(f => f ? { ...f, prompt: f.prompt + `{{${v}}}` } : f);
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Prompt Node</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Prompt Name</Label>
              <Input
                value={form.name}
                onChange={e => setForm(f => f ? { ...f, name: e.target.value } : f)}
                placeholder="e.g. Career Summary"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Output Key <span className="text-gray-400 font-normal">(reference in later stages)</span></Label>
              <Input
                value={form.outputKey}
                onChange={e => setForm(f => f ? { ...f, outputKey: e.target.value.replace(/\s+/g, "_").toLowerCase() } : f)}
                placeholder="e.g. career_summary"
                className="mt-1 font-mono text-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Model</Label>
              <Select value={form.model} onValueChange={v => setForm(f => f ? { ...f, model: v } : f)}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MODELS.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Max Tokens</Label>
              <Select
                value={String(form.maxTokens)}
                onValueChange={v => setForm(f => f ? { ...f, maxTokens: Number(v) } : f)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1000, 2000, 4000, 6000, 8000, 10000, 16000, 24000, 32000].map(t => (
                    <SelectItem key={t} value={String(t)}>{t.toLocaleString()}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Variable inserter */}
          <div>
            <Label className="flex items-center gap-1">
              <Info className="w-3.5 h-3.5 text-blue-500" />
              Insert Variable
            </Label>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              <span className="text-xs text-gray-400 self-center">Raw inputs:</span>
              {SYSTEM_VARS.map(v => (
                <button
                  key={v}
                  onClick={() => insertVar(v)}
                  className="text-xs font-mono bg-blue-50 border border-blue-200 text-blue-700 rounded px-2 py-0.5 hover:bg-blue-100 transition-colors"
                >
                  {`{{${v}}}`}
                </button>
              ))}
              {allOutputKeys.filter(k => k !== form.outputKey).length > 0 && (
                <>
                  <span className="text-xs text-gray-400 self-center ml-1">Stage outputs:</span>
                  {allOutputKeys.filter(k => k !== form.outputKey).map(v => (
                    <button
                      key={v}
                      onClick={() => insertVar(v)}
                      className="text-xs font-mono bg-green-50 border border-green-200 text-green-700 rounded px-2 py-0.5 hover:bg-green-100 transition-colors"
                    >
                      {`{{${v}}}`}
                    </button>
                  ))}
                </>
              )}
            </div>
          </div>

          <div>
            <Label>Prompt</Label>
            <Textarea
              value={form.prompt}
              onChange={e => setForm(f => f ? { ...f, prompt: e.target.value } : f)}
              placeholder={"Write your prompt here. Use {{RESUME}}, {{JOB_DESCRIPTION}}, or any output key from a previous stage like {{organized_jd}}"}
              className="mt-1 font-mono text-sm min-h-[280px] resize-y"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { onSave(form); onClose(); }}>Save Node</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function AdminPipelineBuilder() {
  const { toast } = useToast();
  const [pipeline, setPipeline] = useState<CustomPipeline>(DEFAULT_PIPELINE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingNode, setEditingNode] = useState<{ stageId: string; node: PromptNode } | null>(null);
  const dragRef = useRef<{ stageId: string; nodeId: string } | null>(null);

  // ── Load from Supabase ─────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const { data } = await supabase
          .from("app_settings")
          .select("value")
          .eq("key", "simple_pipeline_config")
          .single();
        if (data?.value) {
          const parsed = JSON.parse(data.value) as CustomPipeline;
          setPipeline(parsed);
        }
      } catch {
        // table may not exist yet — use default
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ── Save to Supabase ───────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      await supabase.from("app_settings").upsert({
        key: "simple_pipeline_config",
        value: JSON.stringify(pipeline),
        updated_at: new Date().toISOString(),
      });
      toast({ title: "Pipeline saved", description: "The Simple tailoring flow will use this pipeline." });
    } catch {
      toast({ title: "Save failed", description: "Make sure the app_settings table exists in Supabase.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  // ── Stage operations ───────────────────────────────────────────────────────
  const addStage = () => {
    setPipeline(p => ({ ...p, stages: [...p.stages, EMPTY_STAGE()] }));
  };

  const deleteStage = (stageId: string) => {
    setPipeline(p => ({ ...p, stages: p.stages.filter(s => s.id !== stageId) }));
  };

  const renameStage = (stageId: string, label: string) => {
    setPipeline(p => ({
      ...p,
      stages: p.stages.map(s => s.id === stageId ? { ...s, label } : s),
    }));
  };

  // ── Node operations ────────────────────────────────────────────────────────
  const addNode = (stageId: string) => {
    const node = EMPTY_NODE();
    setPipeline(p => ({
      ...p,
      stages: p.stages.map(s => s.id === stageId ? { ...s, nodes: [...s.nodes, node] } : s),
    }));
    setEditingNode({ stageId, node });
  };

  const deleteNode = (stageId: string, nodeId: string) => {
    setPipeline(p => ({
      ...p,
      stages: p.stages.map(s =>
        s.id === stageId ? { ...s, nodes: s.nodes.filter(n => n.id !== nodeId) } : s
      ),
    }));
  };

  const saveNode = (stageId: string, updated: PromptNode) => {
    setPipeline(p => ({
      ...p,
      stages: p.stages.map(s =>
        s.id === stageId
          ? { ...s, nodes: s.nodes.map(n => n.id === updated.id ? updated : n) }
          : s
      ),
    }));
  };

  // ── Drag & Drop ────────────────────────────────────────────────────────────
  const handleDragStart = (e: React.DragEvent, stageId: string, nodeId: string) => {
    dragRef.current = { stageId, nodeId };
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  // Drop ON a specific node (insert before it)
  const handleDrop = (e: React.DragEvent, targetStageId: string, targetNodeId?: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragRef.current) return;
    const { stageId: srcStageId, nodeId: srcNodeId } = dragRef.current;
    if (srcStageId === targetStageId && srcNodeId === targetNodeId) return;

    setPipeline(p => {
      // Find and remove the node from source
      let movedNode: PromptNode | undefined;
      const newStages = p.stages.map(s => {
        if (s.id === srcStageId) {
          movedNode = s.nodes.find(n => n.id === srcNodeId);
          return { ...s, nodes: s.nodes.filter(n => n.id !== srcNodeId) };
        }
        return s;
      });
      if (!movedNode) return p;

      // Insert into target
      return {
        ...p,
        stages: newStages.map(s => {
          if (s.id !== targetStageId) return s;
          if (!targetNodeId) return { ...s, nodes: [...s.nodes, movedNode!] };
          const idx = s.nodes.findIndex(n => n.id === targetNodeId);
          const nodes = [...s.nodes];
          nodes.splice(idx, 0, movedNode!);
          return { ...s, nodes };
        }),
      };
    });
    dragRef.current = null;
  };

  // Drop ON the stage column (append to end)
  const handleDropOnStage = (e: React.DragEvent, targetStageId: string) => {
    handleDrop(e, targetStageId);
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400">
        Loading pipeline…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Pipeline Builder</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Stages run left-to-right (sequential). Nodes inside a stage run simultaneously.
            Drag nodes between stages. Click ✏ to edit a node's prompt and model.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Input
            value={pipeline.name}
            onChange={e => setPipeline(p => ({ ...p, name: e.target.value }))}
            className="w-48 font-semibold"
            placeholder="Pipeline name"
          />
          <Button onClick={handleSave} disabled={saving} className="gap-2">
            <Save className="w-4 h-4" />
            {saving ? "Saving…" : "Save Pipeline"}
          </Button>
        </div>
      </div>

      {/* Variable reference card */}
      <Card className="border-blue-100 bg-blue-50">
        <CardContent className="pt-4 pb-3">
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs">
            <span className="font-semibold text-blue-700">Raw inputs available in any prompt:</span>
            {SYSTEM_VARS.map(v => (
              <code key={v} className="text-blue-600 font-mono">{`{{${v}}}`}</code>
            ))}
            <span className="font-semibold text-green-700 ml-4">Stage outputs (use in later stages):</span>
            {pipeline.stages.flatMap(s => s.nodes).map(n => (
              <code key={n.id} className="text-green-600 font-mono">{`{{${n.outputKey}}}`}</code>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Pipeline canvas */}
      <div className="overflow-x-auto pb-6">
        <div className="flex items-start gap-0 min-w-max">
          {pipeline.stages.map((stage, idx) => (
            <StageColumn
              key={stage.id}
              stage={stage}
              index={idx}
              total={pipeline.stages.length}
              pipeline={pipeline}
              onAddNode={addNode}
              onDeleteStage={deleteStage}
              onRenameStage={renameStage}
              onEditNode={(stageId, node) => setEditingNode({ stageId, node })}
              onDeleteNode={deleteNode}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onDropOnStage={handleDropOnStage}
            />
          ))}

          {/* Add Stage button */}
          <div className="flex items-center self-center ml-4 mt-4">
            {pipeline.stages.length > 0 && (
              <ArrowRight className="w-5 h-5 text-gray-200 mr-2" />
            )}
            <button
              onClick={addStage}
              className="w-48 h-28 rounded-xl border-2 border-dashed border-gray-300 hover:border-primary hover:bg-primary/5 transition-colors flex flex-col items-center justify-center gap-2 text-gray-400 hover:text-primary"
            >
              <Plus className="w-6 h-6" />
              <span className="text-sm font-medium">Add Stage</span>
            </button>
          </div>
        </div>
      </div>

      {/* Execution summary */}
      <Card>
        <CardHeader className="pb-2">
          <h3 className="font-semibold text-sm flex items-center gap-2">
            <Play className="w-4 h-4 text-green-500" />
            Execution Order
          </h3>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            {pipeline.stages.map((stage, idx) => (
              <div key={stage.id} className="flex items-center gap-2">
                <div className="flex flex-col items-center gap-1">
                  <span className="text-xs text-gray-400">{stage.label}</span>
                  <div className="flex gap-1">
                    {stage.nodes.length === 0 ? (
                      <span className="text-xs text-gray-300 italic">empty</span>
                    ) : (
                      stage.nodes.map(n => (
                        <Badge key={n.id} variant="outline" className="text-xs py-0 h-6">
                          {n.name}
                        </Badge>
                      ))
                    )}
                  </div>
                </div>
                {idx < pipeline.stages.length - 1 && (
                  <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Node editor dialog */}
      <NodeEditorDialog
        node={editingNode?.node ?? null}
        pipeline={pipeline}
        open={!!editingNode}
        onClose={() => setEditingNode(null)}
        onSave={updated => {
          if (editingNode) saveNode(editingNode.stageId, updated);
        }}
      />
    </div>
  );
}
